import { useState } from 'react';

import { useCapability } from '../../../../hooks/use-capability';
import { usePresence } from '../../../../hooks/use-presence';
import { useAuthStore } from '../../../../stores/auth-store';
import { PageHead, SlaCategoryRow, SlaHeatmapChart, SlaRankingTable } from '../../components';
import {
  useSlaDashboard,
  useSlaDefinitions,
  useSlaHeatmap,
  useSlaRanking,
} from '../../hooks/use-sla';
import { FilterChip } from '../inbox-time/FilterChip';

import { SlaDrillDownModal } from './SlaDrillDownModal';
import { SlaPatternsBanner } from './SlaPatternsBanner';
import { adaptCard, adaptHeatmap, adaptRanking } from './tela5-adapter';

import type { SlaRowVM, SupervisorVariant } from '../../view-models';
import type React from 'react';

type SlaPeriod = 'hoje' | '7d' | '30d';

const PERIOD_OPTS: { value: SlaPeriod; label: string }[] = [
  { value: 'hoje', label: 'Hoje' },
  { value: '7d', label: '7 dias' },
  { value: '30d', label: '30 dias' },
];

function periodDays(p: SlaPeriod): number {
  return p === 'hoje' ? 1 : p === '7d' ? 7 : 30;
}

function periodColLabel(p: SlaPeriod): string {
  return p === 'hoje' ? 'Hoje' : p === '7d' ? '7d' : '30d';
}

function periodSubtitleLabel(p: SlaPeriod): string {
  return p === 'hoje' ? 'hoje' : p === '7d' ? '7 dias' : '30 dias';
}

function mapTier(pkg: string | undefined): SupervisorVariant {
  if (pkg === 'solo') return 'solo';
  if (pkg === 'regulated') return 'regulated';
  return 'team-pro';
}

function SoloEmpty(): React.JSX.Element {
  return (
    <>
      <PageHead title="Painel de SLA" />
      <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--fg-3)' }}>
        <div style={{ fontSize: 48 }}>🔒</div>
        <h3 style={{ color: 'var(--fg-1)' }}>SLA tracking requer Team-Pro+</h3>
        <p>
          Metas de SLA por tenant ainda não podem ser definidas pela interface — fale com a equipe
          Lume.
        </p>
      </div>
    </>
  );
}

export function PainelSlaPage(): React.JSX.Element {
  const variant = mapTier(useAuthStore((s) => s.tenant?.packageType));
  const canRead = useCapability('sla-read');

  const [selectedSlaId, setSelectedSlaId] = useState<string | null>(null);
  const [drillSla, setDrillSla] = useState<SlaRowVM | null>(null);
  const [period, setPeriod] = useState<SlaPeriod>('30d');
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);

  // Todos os hooks ANTES de qualquer early return (rules-of-hooks).
  const dashboard = useSlaDashboard();
  const definitions = useSlaDefinitions();
  // O período re-escopa apenas o ranking (backend dashboard/heatmap não têm param de período).
  const rankingMain = useSlaRanking(periodDays(period));
  const rankingShort = useSlaRanking(7);
  const { data: presence } = usePresence();

  const thresholdByDefId = new Map<string, number>(
    (definitions.data?.data ?? []).map((d) => [d.id, d.thresholdMinutes]),
  );
  const cards = (dashboard.data?.cards ?? []).map((c) => adaptCard(c, thresholdByDefId));
  const worstSlaType = dashboard.data?.topMetrics.worstSlaType ?? null;
  const defaultCard = cards.find((c) => c.slaType === worstSlaType) ?? cards[0];
  const selectedSla = cards.find((c) => c.id === selectedSlaId) ?? defaultCard;

  const heatmap = useSlaHeatmap(selectedSla?.id);

  if (variant === 'solo' || !canRead) return <SoloEmpty />;

  // Fallback de nome via presence (Engine já resolve no ranking) — nunca id cru.
  const nameFor = (userId: string): string | undefined =>
    presence.find((p) => p.userId === userId)?.name ?? undefined;

  const heatmapVM = adaptHeatmap(heatmap.data?.data ?? []);
  const rankingVM = adaptRanking(
    rankingMain.data?.data ?? [],
    rankingShort.data?.data ?? [],
    nameFor,
  );

  // D9 — Categoria: filtra a lista de cards client-side. Heatmap e ranking
  // não são escopados por categoria (são por SLA selecionado / por operador),
  // logo permanecem sobre o conjunto completo — degradação honesta.
  const categoryOpts = cards.map((c) => ({ value: c.slaType, label: c.label }));
  const visibleCards =
    categoryFilter !== undefined ? cards.filter((c) => c.slaType === categoryFilter) : cards;

  const avgCumprimento =
    cards.length > 0 ? Math.round(cards.reduce((a, c) => a + c.cumprimento, 0) / cards.length) : 0;

  const isError =
    dashboard.isError || definitions.isError || rankingMain.isError || rankingShort.isError;
  const isEmpty = !dashboard.isLoading && cards.length === 0;

  return (
    <>
      <PageHead
        title="Painel de SLA"
        subtitle={
          isEmpty ? (
            'Nenhum SLA configurado'
          ) : (
            <>
              {`${cards.length.toString()} SLAs configurados · cumprimento médio ${avgCumprimento.toString()}% · período ${periodSubtitleLabel(period)}`}
              <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--fg-3)' }}>
                O período re-escopa apenas o ranking de operadores; cards e heatmap refletem a
                janela padrão do backend (7 dias).
              </span>
            </>
          )
        }
        filters={
          isEmpty || isError ? undefined : (
            <div className="ti-filters">
              {PERIOD_OPTS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={'chip' + (period === opt.value ? ' chip--active' : '')}
                  onClick={() => {
                    setPeriod(opt.value);
                  }}
                >
                  {opt.label}
                </button>
              ))}
              <FilterChip
                label="Categoria"
                options={categoryOpts}
                value={categoryFilter}
                onChange={setCategoryFilter}
              />
            </div>
          )
        }
      />

      {isError && (
        <div className="banner banner--danger" role="alert">
          <div className="banner__body">
            <span className="banner__title">Falha ao carregar o painel de SLA.</span>
          </div>
        </div>
      )}

      {!isEmpty && !isError && (
        <div className="tela5-body">
          <section>
            <h2 className="sla-section-title">SLAs configurados</h2>
            {visibleCards.map((sla) => (
              <SlaCategoryRow key={sla.id} sla={sla} onDrill={setDrillSla} />
            ))}
          </section>

          <section>
            <h2 className="sla-section-title">Heatmap de SLA por hora × dia da semana</h2>
            {selectedSla !== undefined && (
              <div className="ti-filters" style={{ marginBottom: 'var(--space-2)' }}>
                <label
                  htmlFor="sla-heatmap-select"
                  style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-2)' }}
                >
                  SLA:
                </label>
                <select
                  id="sla-heatmap-select"
                  className="chip"
                  value={selectedSla.id}
                  onChange={(e) => {
                    setSelectedSlaId(e.target.value);
                  }}
                >
                  {cards.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <SlaHeatmapChart heatmap={heatmapVM} />
          </section>

          <SlaPatternsBanner slaDefinitionId={selectedSla?.id} variant={variant} />

          <section>
            <h2 className="sla-section-title">Operadores com mais violações de SLA</h2>
            <SlaRankingTable
              rows={rankingVM}
              mainLabel={`Violações ${periodColLabel(period)}`}
              shortLabel="Violações 7d"
            />
          </section>
        </div>
      )}

      {isEmpty && !isError && (
        <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--fg-3)' }}>
          <div style={{ fontSize: 48 }}>✅</div>
          <h3 style={{ color: 'var(--fg-1)' }}>Este ambiente ainda não tem metas de SLA.</h3>
          {/* A-25 (noturna 04/Set/2026): a versão anterior mandava «configurar via Admin» — e não
              existe onde: o hub Admin não tem item de SLA e o engine só expõe GET /sla-definitions. */}
          <p>
            A definição de SLA por tenant ainda não está disponível na interface — fale com a equipe
            Lume.
          </p>
        </div>
      )}

      {drillSla !== null && (
        <SlaDrillDownModal
          sla={drillSla}
          onClose={() => {
            setDrillSla(null);
          }}
        />
      )}
    </>
  );
}
